<template>
  <div
    class="loader-container"
    :class="{ 'show': viewOn, 'hidden': !viewOn }"
  >
    <div class="loading">
      <v-progress-circular
        indeterminate
        color="primary"
      />
      {{ loading }}
      <template v-if="showResetButton">
        <p class="reset-warning">
          The app appears to be stuck. You can reset to recover.
        </p>
        <v-btn
          color="error"
          size="small"
          @click="resetApp"
        >
          App Reset
        </v-btn>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const props = defineProps({
  viewOn: {
    type: Boolean,
    default: false,
  },
});

const loading = ref('Loading...');
const showResetButton = ref(false);
let resetTimer: ReturnType<typeof setTimeout> | null = null;

const route = useRoute();
const authStore = useAuthStore();

watch(() => props.viewOn, (isActive) => {
  if (isActive) {
    resetTimer = setTimeout(() => {
      showResetButton.value = true;
    }, 20000);
  } else {
    if (resetTimer !== null) {
      clearTimeout(resetTimer);
      resetTimer = null;
    }
    showResetButton.value = false;
  }
});

const resetApp = async () => {
  Sentry.captureMessage('User triggered App Reset after loading screen stuck >20s', {
    level: 'error',
    extra: {
      platform: Capacitor.getPlatform(),
      route: route.fullPath,
      isLoggedIn: authStore.isLoggedIn,
    },
  });

  await Sentry.flush(2000);

  await Preferences.remove({ key: 'access_token' });
  await Preferences.remove({ key: 'refresh_token' });

  window.location.reload();
};
</script>

<style scoped>
.loader-container {
  position: fixed;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
}

.curved-div > * {
  display: inline-block;
}

.loading {
  font-size: 1.8rem;
  padding: 1rem;
  border-radius: .3rem;
  box-shadow: 0 0 .1rem #fefefe;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.reset-warning {
  font-size: 1rem;
  text-align: center;
  color: #ffcccc;
  margin: 0;
}

.show {
  visibility: visible;
  opacity: 1;
  z-index: 2147483647;
  transition: opacity 0.5s ease-out, visibility 0.5s ease-out, z-index 0.5s ease-out;
}

.hidden {
  opacity: 0;
  visibility: hidden;
  z-index: 0;
  transition: opacity 0.5s ease-out, visibility 0.5s ease-out, z-index 0.5s ease-out;
}

@keyframes rotate {
  0% {
    transform: rotateZ(0deg);
  }
  100% {
    transform: rotateZ(360deg);
  }
}
</style>
